import React, { useState, useContext, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Button,
  MenuItem,
  Badge,
  ListItemIcon,
  Menu,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Chat as ChatIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete,
  History as HistoryIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Bookmark as BookmarkIcon,
  IosShare as IosShareIcon,
} from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BookmarkDialog from "../../../dialogs/BookmarkDialog";
import { CMSContext } from "../context/CaseManagmentContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { UploadDocument } from "../../../dialogs/UploadDocumentDialog";
import moment from "moment/moment";
import { toast } from "react-toastify";
import ConfirmationDialog from "../../../dialogs/ConfirmationDialog";
import DocumentHistoryDialog from "../../../dialogs/DocumentHistoryDialog";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteDocumentApi,
  downloadFileApi,
} from "../../../services/api/caseManagementApi";
import { handleShare } from "../../../utils/HelperFunction/utils";
import { fetchBookmarkGroups } from "../../../store/Bookmarks/bookmarksActions";

export default function DocumentManagement({ setIsRightSidebarVisible }) {
  const {
    setData,
    documents,
    fetchCaseDocLoading,
    selectedCase,
    fetchDocuments,
    selectedDocument,
    setSelectedDocument,
  } = useContext(CMSContext);

  const dispatch = useDispatch();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const ownerId = userInfo._id;

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  // null = case-level history, string documentId = per-document history
  const [historyFilter, setHistoryFilter] = useState(null);
  const [openBookmarkModal, setOpenBookmarkModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [bookmarkedDocIds, setBookmarkedDocIds] = useState(new Set());
  const [bookmarkTargetDoc, setBookmarkTargetDoc] = useState(null);

  const caseAdvocate = selectedCase?.advocates?.find(
    (advocate) => advocate.userId === ownerId,
  );

  const isOwner =
    selectedCase?.createdBy === ownerId || caseAdvocate?.role === "Owner";

  useEffect(() => {
    dispatch(fetchBookmarkGroups(userInfo._id));
  }, []);

  const handleOpen = (task) => {
    setEditingDocument(task || null);
  };

  const handleOpenBookmarkModal = (doc) => {
    setBookmarkTargetDoc({
      ...doc,
      id: doc._id,
      documentId: doc.documentId,
      caseId: selectedCase?._id,
    });
    setOpenBookmarkModal(true);
    setAnchorEl(null);
  };

  const handleCloseBookmarkModal = () => {
    setOpenBookmarkModal(false);
    setBookmarkTargetDoc(null);
  };

  const extractFileName = (url) => {
    if (!url) return "";
    const parts = url.split("/");
    const fileNameWithUUID = parts[parts.length - 1];
    const fileNameParts = fileNameWithUUID.split("-");
    return fileNameParts[fileNameParts.length - 1];
  };

  const handleDownload = async (fileUrl) => {
    try {
      const response = await downloadFileApi(fileUrl);
      if (response.status !== 200)
        throw new Error(`HTTP error! status: ${response.status}`);

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = extractFileName(fileUrl);
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed. Please try again.");
    }
  };

  const handleDelete = async (documentId) => {
    const documentToDelete =
      taskToDelete ||
      selectedDocument ||
      documents?.find((item) => item?._id === documentId);

    try {
      setLoading(true);

      const response = await deleteDocumentApi(selectedCase?._id, documentId);

      if (response.status === 200) {
        toast.success("Document deleted successfully");
        fetchDocuments();
        setDeleteModal(false);
        setTaskToDelete(null);
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Failed to delete document");
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentPreview = (fileUrl, fileType) => {
    if (!fileUrl) return;

    if (fileType === "application/pdf" || fileType?.startsWith("image/")) {
      window.open(fileUrl, "_blank");
      return;
    }

    if (
      fileType === "application/msword" ||
      fileType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      window.open(
        `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`,
        "_blank",
      );
      return;
    }

    window.open(fileUrl, "_blank");
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          sx={{ fontSize: "18px", fontWeight: "bold", color: "#003D78" }}
        >
          Documents
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => {
              setHistoryFilter(null);
              setHistoryDialogOpen(true);
            }}
            sx={{
              color: "#003D78",
              borderColor: "#003D78",
              textTransform: "capitalize",
            }}
          >
            History
          </Button>
          {isOwner && (
            <Button
              size="small"
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              sx={{
                backgroundColor: "#003D78",
                color: "#FFFFFF",
                textTransform: "capitalize",
              }}
            >
              Upload
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer
        sx={{
          maxHeight: "300px",
          overflowY: "auto",
          overflowX: "auto",
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          "::-webkit-scrollbar": { width: "5px" },
          "::-webkit-scrollbar-track": { background: "#fff" },
          "::-webkit-scrollbar-thumb": {
            background: "#bbb",
            borderRadius: "6px",
          },
          "::-webkit-scrollbar:horizontal": { height: "4px" },
          "::-webkit-scrollbar-thumb:horizontal": {
            background: "#bbb",
            borderRadius: "6px",
          },
        }}
        component={Paper}
      >
        <Table>
          <TableHead sx={{ backgroundColor: "#003D78" }}>
            <TableRow>
              {[
                "Title",
                "Description",
                "Uploaded By",
                "Timestamp",
                "Comments",
                "Actions",
              ].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    maxWidth: col === "Description" ? 250 : 200,
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: "13px",
                    py: "6px",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchCaseDocLoading ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{ textAlign: "center", padding: "16px" }}
                >
                  <CircularProgress size={18} sx={{ color: "#003D78" }} />
                </TableCell>
              </TableRow>
            ) : Array.isArray(documents) && documents.length > 0 ? (
              documents.map((item) => (
                <React.Fragment key={item._id}>
                  <TableRow sx={{ py: "0px", cursor: "pointer" }}>
                    <TableCell
                      sx={{ fontSize: "13px", py: "3px", maxWidth: 200 }}
                    >
                      <Typography
                        onClick={() =>
                          handleDocumentPreview(item.fileUrl, item.fileType)
                        }
                        sx={{
                          fontSize: "13px",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            color: "#0000FF",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {item.title}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "13px", py: "3px", maxWidth: 250 }}
                    >
                      <Tooltip
                        title={item.description}
                        placement="bottom"
                        arrow
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "13px", py: "3px", maxWidth: 200 }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          width: "90%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.uploadedByName || item.uploadedBy}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        py: "3px",
                        paddingLeft: "30px",
                        maxWidth: 200,
                        "@media (min-width: 1920px)": {
                          paddingLeft: "16px",
                          paddingRight: "32px",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {moment(item.createdAt || item.timestamp).format("ll")}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: "3px" }}>
                      <IconButton
                        color="primary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setData(item);
                          setSelectedDocument(item);
                          setIsRightSidebarVisible(true);
                        }}
                      >
                        <Badge
                          badgeContent={item.comments?.length || 0}
                          color="primary"
                          sx={{
                            "& .MuiBadge-badge": {
                              right: 1,
                              top: 1,
                              fontSize: "0.5rem",
                              minWidth: "13px",
                              height: "13px",
                              padding: "0",
                            },
                          }}
                        >
                          <ChatIcon sx={{ fontSize: "16px" }} />
                        </Badge>
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          sx={{
                            color: "inherit",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                            setSelectedDocument(item);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={() => setAnchorEl(null)}
                          anchorOrigin={{ vertical: "top", horizontal: "left" }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          PaperProps={{
                            sx: {
                              boxShadow:
                                "0px 4px 12px rgba(0, 0, 0, 0.1) !important",
                              borderRadius: "8px",
                            },
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              handleOpen(selectedDocument);
                              setAnchorEl(null);
                              setUploadDialogOpen(true);
                            }}
                          >
                            <ListItemIcon>
                              <FontAwesomeIcon
                                size="xs"
                                icon={faPenToSquare}
                                style={{ color: "#2196f3" }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Edit
                            </Typography>
                          </MenuItem>

                          <MenuItem
                            onClick={() => {
                              setTaskToDelete(selectedDocument);
                              setDeleteModal(true);
                              setAnchorEl(null);
                            }}
                          >
                            <ListItemIcon size="xs">
                              <Delete
                                fontSize="small"
                                sx={{ color: "#d2042d", fontSize: "16px" }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Delete
                            </Typography>
                          </MenuItem>

                          <MenuItem
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setAnchorEl(null);
                              handleDownload(selectedDocument?.fileUrl);
                            }}
                          >
                            <ListItemIcon size="xs">
                              <IconButton sx={{ px: "3px" }} color="primary">
                                <DownloadIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Download
                            </Typography>
                          </MenuItem>

                          <MenuItem
                            onClick={() => {
                              setHistoryFilter(selectedDocument?._id || null);
                              setHistoryDialogOpen(true);
                              setAnchorEl(null);
                            }}
                          >
                            <ListItemIcon size="xs">
                              <HistoryIcon
                                fontSize="small"
                                sx={{ color: "#003D78", fontSize: "16px" }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              History
                            </Typography>
                          </MenuItem>
                        </Menu>
                      </Box>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{
                    textAlign: "center",
                    padding: "16px",
                    fontSize: "14px",
                    color: "#777",
                  }}
                >
                  No Document Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UploadDocument
        uploadDialogOpen={uploadDialogOpen}
        setUploadDialogOpen={setUploadDialogOpen}
        editDocument={editingDocument}
        setEditingDocument={setEditingDocument}
      />

      <ConfirmationDialog
        open={deleteModal}
        onClose={() => {
          setTaskToDelete(null);
          setDeleteModal(false);
        }}
        onConfirm={() => handleDelete(taskToDelete?._id)}
        title="Confirm Delete"
        contentText={`Are you sure you want to delete the document "${taskToDelete?.title}"? This action cannot be undone.`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        loading={loading}
      />

      <DocumentHistoryDialog
        open={historyDialogOpen}
        onClose={() => {
          setHistoryDialogOpen(false);
          setHistoryFilter(null);
        }}
        documents={documents}
        initialFilter={historyFilter}
        onView={handleDocumentPreview}
        onDownload={handleDownload}
        onShare={(entry) =>
          handleShare(entry, (message) =>
            toast.error(message, { position: "top-right" }),
          )
        }
        onBookmark={handleOpenBookmarkModal}
      />

      {openBookmarkModal && (
        <BookmarkDialog
          open={openBookmarkModal}
          onClose={handleCloseBookmarkModal}
          selectedCase={bookmarkTargetDoc}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          setBookmarkedCaseIds={setBookmarkedDocIds}
        />
      )}
    </Box>
  );
}
